<template>
  <v-card ref="paperToolbarContainer" elevation="6" class="paper-toolbar-container" :style="paperToolbarStyle">
    <v-toolbar flat density="compact">
      <v-icon v-show="isDraggable" ref="dragicon" color="font" data-testid="paperToolbar.drag-icon">mdi-drag</v-icon>
      <v-tooltip location="bottom">
        <template #activator="{ props }">
          <div v-bind="props">
            <v-btn-icon icon="mdi-undo" :disabled="!hasUndo" class="px-sm-5 px-4" @click="clickUndo()"> </v-btn-icon>
          </div>
        </template>
        <span>Undo</span>
      </v-tooltip>

      <v-tooltip location="bottom">
        <template #activator="{ props }">
          <div v-bind="props">
            <v-btn-icon icon="mdi-redo" :disabled="!hasRedo" class="px-sm-5 px-4" @click="clickRedo()"> </v-btn-icon>
          </div>
        </template>
        <span>Redo</span>
      </v-tooltip>
      <v-tooltip v-if="layoutEnabled" location="bottom">
        <template #activator="{ props }">
          <div v-bind="props">
            <v-btn icon="mdi-arrange-send-to-back" color="'primary'" class="px-sm-5 px-4" @click="layout()"> </v-btn>
          </div>
        </template>
        <span>Layout</span>
      </v-tooltip>
      <v-btn icon="true" class="px-sm-5 px-4">
        <v-icon>mdi-file-document-edit-outline</v-icon>
        <v-speed-dial location="bottom center" activator="parent" open-on-hover>
          <TransitionGroup name="noop" :appear="false">
            <v-btn
              v-tooltip="{ location: 'left', text: 'Show Expressions' }"
              icon
              class="px-sm-5 px-4"
              @click="onShowExpressions()"
            >
              <v-icon>mdi-code-braces</v-icon>
            </v-btn>
            <v-btn
              v-tooltip="{ location: 'left', text: 'Show Documentation' }"
              icon
              class="px-sm-5 px-4"
              @click="onShowDocumentation()"
            >
              <v-icon>mdi-text-box</v-icon>
            </v-btn>
            <v-btn
              v-tooltip="{ location: 'left', text: 'Show Documentation if present' }"
              icon
              class="px-sm-5 px-4"
              @click="onShowDocumentationIfPresent()"
            >
              <v-icon>mdi-file-document-check</v-icon>
            </v-btn>
          </TransitionGroup>
        </v-speed-dial>
      </v-btn>
      <v-btn icon="true" class="px-sm-5 px-4">
        <v-icon>mdi-dots-vertical</v-icon>
        <v-speed-dial location="bottom center" activator="parent" open-on-hover>
          <TransitionGroup name="noop" :appear="false">
            <v-btn
              v-tooltip="{
                location: 'left',
                text: preferenceStore.showTransitionPriorities ? 'Hide Transition Prios' : 'Show Transition Prios',
              }"
              icon="mdi-table-of-contents mdi-rotate-180"
              class="px-sm-5 px-4"
              @click="showTransitionPriorities()"
            >
              <v-icon :color="preferenceStore.showTransitionPriorities ? 'primary' : 'font'"
                >mdi-table-of-contents mdi-rotate-180</v-icon
              >
            </v-btn>
            <v-btn
              v-tooltip="{
                location: 'left',
                text: preferenceStore.enableSnaplines ? 'Turn off Snaplines' : 'Turn on Snaplines',
              }"
              icon
              class="px-sm-5 px-4"
              @click="enableSnaplines()"
            >
              <v-icon :color="preferenceStore.enableSnaplines ? 'primary' : 'font'">mdi-angle-right</v-icon>
            </v-btn>

            <v-menu
              :close-on-content-click="false"
              location="left"
              :open-on-hover="false"
              offset="8"
              class="overflow-visible"
            >
              <template #activator="{ props }">
                <v-btn
                  id="grid-activator"
                  v-tooltip="{
                    location: 'left',
                    text: 'Configure Grid',
                  }"
                  icon
                  class="px-sm-5 px-4"
                  v-bind="props"
                >
                  <v-icon :color="preferenceStore.gridEnabled ? 'primary' : 'font'">mdi-view-comfy</v-icon>
                </v-btn>
              </template>
              <v-list width="200px">
                <v-list-item>
                  <v-checkbox v-model="preferenceStore.gridEnabled" hide-details label="Enable Grid"></v-checkbox>
                </v-list-item>
                <v-list-item>
                  <v-checkbox
                    v-model="preferenceStore.gridShow"
                    hide-details
                    :disabled="!preferenceStore.gridEnabled"
                    label="Show Grid"
                  ></v-checkbox>
                </v-list-item>
                <v-list-item>
                  <v-slider
                    v-model="preferenceStore.gridSize"
                    :disabled="!preferenceStore.gridEnabled"
                    label="Size"
                    min="1"
                    max="50"
                    step="1"
                    track-fill-color="primary"
                    track-color="grey"
                    thumb-color="primary"
                    thumb-label="always"
                    thumb-size="12px"
                  ></v-slider>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-menu
              v-if="layoutEnabled"
              :close-on-content-click="false"
              location="left"
              :open-on-hover="false"
              offset="8"
              class="overflow-visible"
              @update:modelValue="onLayoutMenuUpdate"
            >
              <template #activator="{ props }">
                <v-btn
                  id="layout-activator"
                  v-tooltip="{ location: 'left', text: 'Configure Layouter' }"
                  icon
                  color="'primary'"
                  class="px-sm-5 px-4"
                  v-bind="props"
                >
                  <v-icon>mdi-vector-polyline</v-icon>
                </v-btn>
              </template>
              <v-list width="300px">
                <v-list-item style="margin-top: 16px">
                  <v-slider
                    v-model="preferenceStore.horizontalNodeDistance"
                    label="Horizontal distance"
                    min="20"
                    max="300"
                    step="1"
                    track-fill-color="primary"
                    track-color="grey"
                    thumb-color="primary"
                    thumb-label="always"
                    thumb-size="12px"
                  />
                </v-list-item>
                <v-list-item>
                  <v-slider
                    v-model="preferenceStore.verticalNodeDistance"
                    label="Vertical distance"
                    min="20"
                    max="300"
                    step="1"
                    track-fill-color="primary"
                    track-color="grey"
                    thumb-color="primary"
                    thumb-label="always"
                    thumb-size="12px"
                  ></v-slider>
                </v-list-item>
                <v-list-item>
                  <v-select
                    :items="possibleRoutingStrategies"
                    :model-value="selectedRouter"
                    label="Apply global transition router"
                    density="compact"
                    variant="outlined"
                    hide-details
                    @update:modelValue="onRouterSelectChange"
                /></v-list-item>
              </v-list>
            </v-menu>
            <v-btn
              v-tooltip="{
                location: 'left',
                text: preferenceStore.isDark ? 'Turn on Light mode' : 'Turn on Dark mode',
              }"
              icon
              class="px-sm-5 px-4"
              @click="switchTheme()"
            >
              <v-icon :color="preferenceStore.isDark ? 'primary' : 'font'">mdi-theme-light-dark</v-icon>
            </v-btn>
          </TransitionGroup>
        </v-speed-dial>
      </v-btn>
    </v-toolbar>
  </v-card>
</template>

<script setup lang="ts">
import DragHandler from "./services/drag-handler";
import { PreferenceStore } from "./services/preference-store";
import RappidContext from "./rappid/rappid-context";
import { GraphLayouter } from "./services/layout/graph-layouter";
import { inject, ref, watch, onMounted, reactive, computed } from "vue";
import { useDisplay } from "vuetify";
import { CardPosition } from "./utils/resizeCard";
import { getConfig } from "../../config";
import { redo, undo } from "./rappid/undo-redo";
import {
  ConfiguredRouterForAllLinks,
  POSSIBLE_ROUTING_STRATEGIES as possibleRoutingStrategies,
  Transition,
} from "./mvc/transition";
import SpecificationElement from "./mvc/base/specification-element";
import DocumentationElement from "./mvc/base/documentation-element";
import { State } from "./mvc/state";

const { layoutEnabled } = getConfig();

const rappidContext: RappidContext = inject<RappidContext>("rappidContext");
const layoutService: GraphLayouter = inject<GraphLayouter>("layoutService");

const paperToolbarContainer = ref(null);

const position = reactive<CardPosition>({
  xLeft: "unset",
  xRight: "unset",
  yTop: "unset",
  yBottom: "unset",
});

const paperToolbarStyle = computed(() => {
  return {
    left: position.xLeft,
    width: "fit-content",
    top: position.yTop,
  };
});

const { smAndUp } = useDisplay();

const dragicon = ref();

const preferenceStore = reactive(PreferenceStore.INSTANCE);

const isDraggable = ref(false);

const hasUndo = ref<boolean>(false);
const hasRedo = ref<boolean>(false);

watch(smAndUp, (newVal: boolean) => {
  isDraggable.value = newVal;
});

onMounted(() => {
  isDraggable.value = smAndUp.value;
  const dragTarget = dragicon.value.$el as HTMLElement;
  const dragContent = paperToolbarContainer.value.$el as HTMLElement;
  new DragHandler(dragTarget, dragContent, position).enableDragging();

  rappidContext.commandManager.on("stack", (_opt) => {
    hasUndo.value = rappidContext.commandManager?.hasUndo();
    hasRedo.value = rappidContext.commandManager?.hasRedo();
  });
});

function onLayoutMenuUpdate(isOpen: boolean) {
  if (isOpen) {
    selectedRouter.value = getCurrentRouterConfig();
  }
}

const selectedRouter = ref<ConfiguredRouterForAllLinks>("orthogonal");

function getCurrentRouterConfig() {
  const graph = rappidContext.graph;
  let retVal: ConfiguredRouterForAllLinks = "orthogonal";
  if (graph) {
    const links = graph.getLinks();
    if (links.length > 0) {
      const firstRouter = (links[0].get("router")?.name as ConfiguredRouterForAllLinks) || "smart (obstacle-avoid)";
      const allSame = links.every(
        (link) =>
          ((link.get("router")?.name as ConfiguredRouterForAllLinks) || "smart (obstacle-avoid)") === firstRouter
      );
      retVal = allSame ? firstRouter : "mixed";
    }
  }
  return retVal;
}

function onRouterSelectChange(newValue: ConfiguredRouterForAllLinks) {
  selectedRouter.value = newValue;
  if (newValue === "mixed") {
    return;
  }
  PreferenceStore.INSTANCE.linkRouter = newValue;
  rappidContext.routerController.setRouterForAllLinks(newValue);
}

function clickUndo() {
  undo(rappidContext);
}

function clickRedo() {
  redo(rappidContext);
}

function enableSnaplines() {
  preferenceStore.enableSnaplines = !preferenceStore.enableSnaplines;
}

function showTransitionPriorities() {
  preferenceStore.showTransitionPriorities = !preferenceStore.showTransitionPriorities;
}

function onShowExpressions() {
  rappidContext.commandManager.initBatchCommand();
  const cells = rappidContext.graph.getCells();
  cells.forEach((cell) => {
    if (cell instanceof State || cell instanceof Transition) {
      cell.setShowSpecification(true);
    }
  });
  rappidContext.commandManager.storeBatchCommand();
}

function onShowDocumentation() {
  rappidContext.commandManager.initBatchCommand();
  const cells = rappidContext.graph.getCells();
  cells.forEach((cell) => {
    if (cell instanceof State || cell instanceof Transition) {
      cell.setShowSpecification(false);
    }
  });
  rappidContext.commandManager.storeBatchCommand();
}

function onShowDocumentationIfPresent() {
  rappidContext.commandManager.initBatchCommand();
  const cells = rappidContext.graph.getCells();
  cells.forEach((cell) => {
    if (cell instanceof State || cell instanceof Transition) {
      const documentation = cell.getDocumentation();
      const hasDocumentation = documentation && documentation.trim().length > 0;
      cell.setShowSpecification(!hasDocumentation);
    }
  });
  rappidContext.commandManager.storeBatchCommand();
}

function layout() {
  layoutService.layout(rappidContext);
  rappidContext.paper.update();
}

function switchTheme() {
  preferenceStore.isDark = !preferenceStore.isDark;
}
</script>

<style scoped>
.paper-toolbar-container {
  position: absolute;
  z-index: 1;
}

:deep(.v-list-item__content) {
  overflow: visible;
}

:deep(.compact-list-item .v-list-item__prepend) {
  margin-inline-end: -20px !important;
}

/* no-op transition - overcoming Vuetify's eagerness to animate the speed-dial menu*/
.noop-enter-active,
.noop-leave-active,
.noop-move {
  transition: none !important;
}
</style>
